import type { ResolvedSignedUrlResponse, SignedUrlResponse } from "@/services/storageService.types";

import {
  CLOCK_SKEW_BUFFER_MS,
  clearAllPersistedSignedUrls,
  readPersistedSignedUrl,
  removePersistedSignedUrl,
  writePersistedSignedUrl,
} from "./signedUrlPersistence";

export type SignedUrlFetcher = () => Promise<SignedUrlResponse>;

type CacheEntry = {
  url: string;
  expiresAtMs: number | null;
};

const cache = new Map<string, CacheEntry>();
const inFlight = new Map<string, Promise<ResolvedSignedUrlResponse>>();

export function buildSignedUrlCacheKey(fileId: string, type: string): string {
  return `${fileId}:${type}`;
}

function parseExpiry(expiresAt?: string): number | null {
  const ms = expiresAt ? Date.parse(expiresAt) : NaN;
  return Number.isFinite(ms) ? ms : null;
}

function resolveSignedUrlResponse(data: SignedUrlResponse): ResolvedSignedUrlResponse {
  if (!data.signed_url) {
    throw new Error("No signed URL");
  }
  return { signed_url: data.signed_url, expires_at: data.expires_at };
}

function isCacheEntryFresh(entry: CacheEntry): boolean {
  return entry.expiresAtMs != null && Date.now() < entry.expiresAtMs - CLOCK_SKEW_BUFFER_MS;
}

export async function getOrFetchSignedUrl(
  key: string,
  fetcher: SignedUrlFetcher
): Promise<ResolvedSignedUrlResponse> {
  const cached = cache.get(key);
  if (cached && isCacheEntryFresh(cached)) {
    return {
      signed_url: cached.url,
      expires_at:
        cached.expiresAtMs != null ? new Date(cached.expiresAtMs).toISOString() : undefined,
    };
  }

  const persisted = readPersistedSignedUrl(key);
  if (persisted) {
    const resolved = resolveSignedUrlResponse(persisted);
    cache.set(key, { url: resolved.signed_url, expiresAtMs: parseExpiry(resolved.expires_at) });
    return resolved;
  }

  const existing = inFlight.get(key);
  if (existing) {
    return existing;
  }

  const promise = (async () => {
    try {
      const data = await fetcher();
      const resolved = resolveSignedUrlResponse(data);
      writePersistedSignedUrl(key, data);
      cache.set(key, { url: resolved.signed_url, expiresAtMs: parseExpiry(resolved.expires_at) });
      return resolved;
    } finally {
      inFlight.delete(key);
    }
  })();

  inFlight.set(key, promise);
  return promise;
}

export function invalidateSignedUrl(key: string): void {
  cache.delete(key);
  removePersistedSignedUrl(key);
}

export function clearSignedUrlCache(): void {
  cache.clear();
  inFlight.clear();
  clearAllPersistedSignedUrls();
}
